import { API } from "../../constAPI";

const throwApiError = (data, fallback) => {
  const error = new Error(data?.detail || data?.message || data?.error || fallback);
  error.code = data?.code || data?.error;
  error.status = data?.status;
  error.requires_action = Boolean(data?.requires_action);
  error.payment_intent_id = data?.payment_intent_id;
  error.data = data;
  throw error;
};

const authedJson = (url, token, options = {}) =>
  fetch(url, {
    ...options,
    headers: {
      Authorization: `token ${token}`,
      Accept: "application/json",
      "Content-type": "application/json",
      ...(options.headers || {}),
    },
  }).then(async (response) => {
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throwApiError(data, "Package refund request failed");
    }
    return data;
  });

export const getPackageRefundRequests = (token, params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") searchParams.append(key, value);
  });
  const query = searchParams.toString();
  return authedJson(`${API}/api/admin/packages/refund-requests${query ? `?${query}` : ""}`, token);
};

export const approvePackageRefundRequest = (token, requestId) =>
  authedJson(`${API}/api/admin/packages/refund-requests/${requestId}/approve`, token, { method: "POST" });

export const rejectPackageRefundRequest = (token, requestId, adminNote = "") =>
  authedJson(`${API}/api/admin/packages/refund-requests/${requestId}/reject`, token, {
    method: "POST",
    body: JSON.stringify({ admin_note: adminNote }),
  });

export const getPackagePurchases = (token, params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, value);
    }
  });
  const query = searchParams.toString();
  const url = query
    ? `${API}/api/admin/packages/purchases?${query}`
    : `${API}/api/admin/packages/purchases`;

  return fetch(url, {
    headers: {
      Authorization: `token ${token}`,
      Accept: "application/json",
      "Content-type": "application/json",
    },
  })
    .then((response) => response.json())
    .catch((error) => console.log(error));
};

export const getPackageCreditsBalance = (token, params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, value);
    }
  });
  const query = searchParams.toString();
  const url = query
    ? `${API}/api/admin/packages/credits/balance?${query}`
    : `${API}/api/admin/packages/credits/balance`;

  return fetch(url, {
    headers: {
      Authorization: `token ${token}`,
      Accept: "application/json",
      "Content-type": "application/json",
    },
  })
    .then((response) => response.json())
    .catch((error) => console.log(error));
};

export const confirmHeldLessonCredit = (token, lessonId, participantId) => {
  return fetch(
    `${API}/api/admin/credits/lessons/${lessonId}/participants/${participantId}/confirm`,
    {
      method: "POST",
      headers: {
        Authorization: `token ${token}`,
        Accept: "application/json",
        "Content-type": "application/json",
      },
    }
  ).then(async (response) => {
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throwApiError(data, "Failed to confirm credit");
    }
    return data;
  });
};

export const useLessonCreditForParticipant = (
  token,
  lessonId,
  participantId,
  purchaseId
) => {
  return fetch(
    `${API}/api/admin/credits/lessons/${lessonId}/participants/${participantId}/use`,
    {
      method: "POST",
      headers: {
        Authorization: `token ${token}`,
        Accept: "application/json",
        "Content-type": "application/json",
      },
      body: JSON.stringify({
        ...(purchaseId ? { purchase_id: purchaseId } : {}),
      }),
    }
  ).then(async (response) => {
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throwApiError(data, "Failed to use credit");
    }
    return data;
  });
};

export const previewLessonCreditForPlayer = (token, lessonId, playerId) => {
  const query = new URLSearchParams({ player_id: playerId });
  return fetch(
    `${API}/api/admin/credits/lessons/${lessonId}/credit-preview?${query}`,
    {
      headers: {
        Authorization: `token ${token}`,
        Accept: "application/json",
        "Content-type": "application/json",
      },
    }
  ).then(async (response) => {
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throwApiError(data, "Failed to preview credit");
    }
    return data;
  });
};

export const useLessonCreditForPlayer = (token, lessonId, playerId, purchaseId) => {
  return fetch(`${API}/api/admin/credits/lessons/${lessonId}/use`, {
    method: "POST",
    headers: {
      Authorization: `token ${token}`,
      Accept: "application/json",
      "Content-type": "application/json",
    },
    body: JSON.stringify({
      player_id: playerId,
      ...(purchaseId ? { purchase_id: purchaseId } : {}),
    }),
  }).then(async (response) => {
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throwApiError(data, "Failed to use credit");
    }
    return data;
  });
};

export const rollbackLessonCreditForParticipant = (token, lessonId, participantId) => {
  return fetch(
    `${API}/api/admin/credits/lessons/${lessonId}/participants/${participantId}/rollback`,
    {
      method: "POST",
      headers: {
        Authorization: `token ${token}`,
        Accept: "application/json",
        "Content-type": "application/json",
      },
    }
  ).then(async (response) => {
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throwApiError(data, "Failed to rollback credit");
    }
    return data;
  });
};

// expiresAt: an ISO date, or null for no expiry.
export const updatePackagePurchaseExpiry = (token, purchaseId, expiresAt) =>
  authedJson(`${API}/api/admin/packages/purchases/${purchaseId}/expiry`, token, {
    method: "PATCH",
    body: JSON.stringify({ expires_at: expiresAt }),
  });
